import React, { useState, useEffect } from 'react';
import { t } from '../../utils/i18n';

const WEEK_DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' }
];

const AttendanceSettings = ({ api }) => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [rawResponse, setRawResponse] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [refsLoading, setRefsLoading] = useState(false);
  const [refsError, setRefsError] = useState(null);
  const [formData, setFormData] = useState({
    entity_type: '',
    entity_id: '',
    work_start_time: '',
    work_end_time: '',
    grace_period_minutes: 0,
    min_hours_half_day: 4,
    min_hours_full_day: 8,
    weekly_off: []
  });

  useEffect(() => {
    fetchSettings();
    // fetch refs on mount so the list can display human-readable entity names
    fetchRefs();
  }, []);

  // when entity_type changes, fetch refs if needed and clear entity_id for global
  useEffect(() => {
    if (formData.entity_type === 'location' && locations.length === 0) {
      fetchRefs();
    }
    if (formData.entity_type === 'department' && departments.length === 0) {
      fetchRefs();
    }
    if (formData.entity_type === 'global') {
      setFormData(prev => ({ ...prev, entity_id: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.entity_type]);

  // when refs load, auto-select first option if none selected (improves UX on first click)
  useEffect(() => {
    if (formData.entity_type === 'location' && locations.length > 0 && !formData.entity_id) {
      setFormData(prev => ({ ...prev, entity_id: locations[0].id }));
    }
  }, [locations]);

  useEffect(() => {
    if (formData.entity_type === 'department' && departments.length > 0 && !formData.entity_id) {
      setFormData(prev => ({ ...prev, entity_id: departments[0].id }));
    }
  }, [departments]);

  const extractHHMM = (isoOrTime) => {
    if (!isoOrTime) return '';
    // If ISO datetime like 2000-01-01T09:00:00.000Z -> return '09:00'
    const idx = String(isoOrTime).indexOf('T');
    if (idx !== -1) {
      const timePart = String(isoOrTime).slice(idx + 1);
      return timePart.slice(0,5);
    }
    // fallback: assume it's already a time-like string
    return String(isoOrTime).slice(0,5);
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await api.get('/attendance_settings');
      console.debug('AttendanceSettings.fetchSettings response:', data);
      setRawResponse(data);
      // API may return nested shapes. normalize to array of settings.
      let list = [];
      if (Array.isArray(data)) {
        list = data;
      } else if (data && Array.isArray(data.attendance_settings)) {
        list = data.attendance_settings;
      } else if (data && data.data && Array.isArray(data.data.settings)) {
        list = data.data.settings;
      } else if (data && Array.isArray(data.settings)) {
        list = data.settings;
      }

      setSettings(list);
      setFetchError(null);
    } catch (err) {
      setRawResponse(null);
      setFetchError(err.message || String(err));
      console.error('Failed to fetch attendance settings', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRefs = async () => {
    setRefsLoading(true);
    setRefsError(null);
    try {
      const [locResp, deptResp] = await Promise.all([
        api.get('/locations'),
        api.get('/departments')
      ]);

      const locs = (locResp && (locResp.data?.locations || locResp.locations)) || locResp || [];
      const depts = (deptResp && (deptResp.data?.departments || deptResp.departments)) || deptResp || [];

      setLocations(Array.isArray(locs) ? locs : []);
      setDepartments(Array.isArray(depts) ? depts : []);
    } catch (err) {
      console.error('Failed to fetch locations/departments', err);
      setRefsError(err.message || String(err));
      setLocations([]);
      setDepartments([]);
    } finally {
      setRefsLoading(false);
    }
  };

  const getEntityLabel = (s) => {
    if (!s) return '-';
    if (s.entity_type === 'global') return 'Global';
    if (!s.entity_id) return `${s.entity_type}`;

    const idStr = String(s.entity_id);
    if (s.entity_type === 'location') {
      const loc = locations.find(l => String(l.id) === idStr);
      return loc ? (loc.name || loc.title || `Location #${s.entity_id}`) : `Location #${s.entity_id}`;
    }

    if (s.entity_type === 'department') {
      const d = departments.find(dep => String(dep.id) === idStr);
      return d ? (d.name || d.title || `Department #${s.entity_id}`) : `Department #${s.entity_id}`;
    }

    // fallback
    return `${s.entity_type}${s.entity_id ? ` (#${s.entity_id})` : ''}`;
  };

  const handleAdd = () => {
    setFormData({
      entity_type: '',
      entity_id: '',
      work_start_time: '09:00',
      work_end_time: '17:00',
      grace_period_minutes: 15,
      min_hours_half_day: 4,
      min_hours_full_day: 8,
      weekly_off: []
    });
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (s) => {
    setFormData({
      entity_type: s.entity_type || '',
      entity_id: s.entity_id || '',
      work_start_time: extractHHMM(s.work_start_time),
      work_end_time: extractHHMM(s.work_end_time),
      grace_period_minutes: s.grace_period_minutes || 0,
      min_hours_half_day: s.min_hours_half_day || 4,
      min_hours_full_day: s.min_hours_full_day || 8,
      weekly_off: Array.isArray(s.weekly_off) ? s.weekly_off : (s.weekly_off ? s.weekly_off.split(',') : [])
    });
    setEditingId(s.id);
    // ensure refs are loaded if editing a location/department setting
    if (s.entity_type === 'location' && locations.length === 0) fetchRefs();
    if (s.entity_type === 'department' && departments.length === 0) fetchRefs();
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this attendance setting?')) return;
    setLoading(true);
    try {
      await api.delete(`/attendance_settings/${id}`);
      setSettings(settings.filter(s => s.id !== id));
    } catch (err) {
      console.error('Failed to delete setting', err);
      alert('Failed to delete attendance setting');
    } finally {
      setLoading(false);
    }
  };

  const toggleWeeklyOff = (dayKey) => {
    const newWeekly = formData.weekly_off.includes(dayKey)
      ? formData.weekly_off.filter(d => d !== dayKey)
      : [...formData.weekly_off, dayKey];
    setFormData({ ...formData, weekly_off: newWeekly });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      attendance_setting: {
        ...formData,
        // Ensure weekly_off stored as array server-side; adapt if API expects CSV
        weekly_off: formData.weekly_off
      }
    };

    try {
      if (editingId) {
        const { response, data } = await api.put(`/attendance_settings/${editingId}`, payload);
        if (response && response.ok) {
          setSettings(settings.map(s => (s.id === editingId ? data : s)));
        } else {
          alert('Failed to update attendance setting');
        }
      } else {
        const { response, data } = await api.post('/attendance_settings', payload);
        if (response && response.ok) {
          setSettings([...settings, data]);
        } else {
          alert('Failed to create attendance setting');
        }
      }
      setShowForm(false);
      setEditingId(null);
    } catch (err) {
      console.error('Failed to save setting', err);
      alert('Failed to save attendance setting');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
  };

  return (
    <div>
      {/* Header */}
      <div className="dFlex baseTableHd mb_16">
        <h2 className="txt20 fwMedium colorBlack">Attendance Settings</h2>
        <div style={{ marginLeft: 'auto' }}>
          <button className="btnAdd" onClick={handleAdd}>Add Setting</button>
        </div>
        {fetchError && (
          <div style={{ padding: '12px', color: 'red' }}>Failed to load settings: {fetchError}</div>
        )}
        {!fetchError && rawResponse && settings.length === 0 && (
          <div style={{ padding: '12px' }}>
            <div style={{ fontWeight: '600', marginBottom: '6px' }}>Debug: raw API response</div>
            <pre style={{ maxHeight: '240px', overflow: 'auto', background: '#f6f6f6', padding: '8px' }}>{JSON.stringify(rawResponse, null, 2)}</pre>
          </div>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="mb_24 baseTableWrap">
          <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
            <div className="row">
              <div className="col-md-4 mb_20">
                <label className="mb_8">Entity Type</label>
                <select
                  className="form-control"
                  value={formData.entity_type}
                  onChange={(e) => setFormData({ ...formData, entity_type: e.target.value })}
                  required
                >
                  <option value="">Select type</option>
                  <option value="global">global</option>
                  <option value="location">location</option>
                  <option value="department">department</option>
                </select>
              </div>

              {/* Entity ID: show select for location/department, info for global */}
              <div className="col-md-4 mb_20">
                <label className="mb_8">Entity</label>
                {formData.entity_type === 'global' && (
                  <input type="text" className="form-control" value="Applies to all locations & departments" disabled />
                )}

                {formData.entity_type === 'location' && (
                  <>
                    {refsLoading ? (
                      <div>Loading locations...</div>
                    ) : refsError ? (
                      <div style={{ color: 'red' }}>Failed to load locations</div>
                    ) : (
                      <select
                        className="form-control"
                        value={formData.entity_id}
                        onChange={(e) => setFormData({ ...formData, entity_id: e.target.value })}
                        required
                      >
                        <option value="">Select location</option>
                        {locations.map(loc => (
                          <option key={loc.id} value={loc.id}>{loc.name || loc.title || `#${loc.id}`}</option>
                        ))}
                      </select>
                    )}
                  </>
                )}

                {formData.entity_type === 'department' && (
                  <>
                    {refsLoading ? (
                      <div>Loading departments...</div>
                    ) : refsError ? (
                      <div style={{ color: 'red' }}>Failed to load departments</div>
                    ) : (
                      <select
                        className="form-control"
                        value={formData.entity_id}
                        onChange={(e) => setFormData({ ...formData, entity_id: e.target.value })}
                        required
                      >
                        <option value="">Select department</option>
                        {departments.map(d => (
                          <option key={d.id} value={d.id}>{d.name || d.title || `#${d.id}`}</option>
                        ))}
                      </select>
                    )}
                  </>
                )}

                {formData.entity_type === '' && (
                  <input type="text" className="form-control" value="Choose entity type" disabled />
                )}
              </div>

              <div className="col-md-4 mb_20">
                <label className="mb_8">Work Start Time</label>
                <input
                  type="time"
                  className="form-control"
                  value={formData.work_start_time}
                  onChange={(e) => setFormData({ ...formData, work_start_time: e.target.value })}
                />
              </div>

              <div className="col-md-4 mb_20">
                <label className="mb_8">Work End Time</label>
                <input
                  type="time"
                  className="form-control"
                  value={formData.work_end_time}
                  onChange={(e) => setFormData({ ...formData, work_end_time: e.target.value })}
                />
              </div>

              <div className="col-md-4 mb_20">
                <label className="mb_8">Grace Period (minutes)</label>
                <input
                  type="number"
                  className="form-control"
                  value={formData.grace_period_minutes}
                  onChange={(e) => setFormData({ ...formData, grace_period_minutes: parseInt(e.target.value || 0, 10) })}
                />
              </div>

              <div className="col-md-4 mb_20">
                <label className="mb_8">Min Hours Half Day</label>
                <input
                  type="number"
                  step="0.25"
                  className="form-control"
                  value={formData.min_hours_half_day}
                  onChange={(e) => setFormData({ ...formData, min_hours_half_day: parseFloat(e.target.value || 0) })}
                />
              </div>

              <div className="col-md-4 mb_20">
                <label className="mb_8">Min Hours Full Day</label>
                <input
                  type="number"
                  step="0.25"
                  className="form-control"
                  value={formData.min_hours_full_day}
                  onChange={(e) => setFormData({ ...formData, min_hours_full_day: parseFloat(e.target.value || 0) })}
                />
              </div>

              <div className="col-12 mb_20">
                <label className="mb_8">Weekly Off</label>
                <div className="dFlex" style={{ gap: '8px', flexWrap: 'wrap' }}>
                  {WEEK_DAYS.map(d => (
                    <label key={d.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input
                        type="checkbox"
                        checked={formData.weekly_off.includes(d.key)}
                        onChange={() => toggleWeeklyOff(d.key)}
                      />
                      <span>{d.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="dFlex mt_18 btnSet">
              <button type="submit" className="btnBig me-3" disabled={loading}>{loading ? 'Saving...' : (editingId ? 'Update' : 'Save')}</button>
              <button type="button" className="btnBig btnSecondary" onClick={handleCancel}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      <div className="baseTableWrap">
        <div className="baseTableContainer">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
          ) : (
            <table className="baseTable">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Entity</th>
                  <th>Work Hours</th>
                  <th>Grace (min)</th>
                  <th>Min Half/Full</th>
                  <th>Weekly Off</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {settings.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>No attendance settings found</td>
                  </tr>
                ) : (
                  settings.map(s => (
                    <tr key={s.id}>
                      <td>{s.id}</td>
                      <td>{getEntityLabel(s)}</td>
                      <td>{s.work_start_time ? extractHHMM(s.work_start_time) : '-'} - {s.work_end_time ? extractHHMM(s.work_end_time) : '-'}</td>
                      <td>{s.grace_period_minutes ?? '-'}</td>
                      <td>{s.min_hours_half_day ?? '-'} / {s.min_hours_full_day ?? '-'}</td>
                      <td>{(Array.isArray(s.weekly_off) ? s.weekly_off : (s.weekly_off ? s.weekly_off.split(',') : [])).join(', ')}</td>
                      <td>
                        <div className="dFlex optBlock">
                          <button className="optBtn" onClick={() => handleEdit(s)}>
                            <img src="/images/edit.png" alt="" />
                            <span>Edit</span>
                          </button>
                          <span className="bar">|</span>
                          <button className="optBtn" onClick={() => handleDelete(s.id)}>
                            <img src="/images/delete.png" alt="" />
                            <span>Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceSettings;
